import axios, { AxiosInstance } from "axios";
import {
  MatchPrediction,
  PredictionSubmission,
  PredictionWinner,
} from "@/types/app";

const BASE_URL = "https://ftx-pay.futurix.xyz";
const WS_URL = "wss://ftx-pay.futurix.xyz/websocket";

export interface DirectusSession {
  id: string;
  status: boolean;
  callBackResponse: any;
}

export interface PaymentIntentResponse {
  paymentIntent: string;
}

export class DirectusService {
  private apiClient: AxiosInstance;

  constructor() {
    this.apiClient = axios.create({
      baseURL: BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });
  }

  async getAppSettings() {
    try {
      const response = await this.apiClient.get("/items/appsetting", {
        params: {
          fields: "*,compte.*",
        },
      });
      return response.data.data;
    } catch (error) {
      console.error("Error fetching app settings:", error);
      throw error;
    }
  }

  async getActiveMatches(): Promise<MatchPrediction[]> {
    try {
      const response = await this.apiClient.get("/items/pronostic", {
        params: {
          fields: "*",
          filter: {
            status: {
              _eq: "published",
            },
          },
        },
      });
      const allMatches = response.data.data || [];
      return allMatches;
    } catch (error: any) {
      // Ne pas logger l'erreur 403 (permissions non configurées)
      if (error?.response?.status !== 403) {
        console.error("Error fetching active matches:", error);
      }
      throw error;
    }
  }

  async submitPrediction(prediction: PredictionSubmission): Promise<any> {
    try {
      console.log("📤 Envoi du pronostic:", prediction);
      const response = await this.apiClient.post(
        "/items/predictions",
        prediction
      );
      console.log("✅ Réponse du serveur:", response.data);
      return response.data;
    } catch (error: any) {
      console.error("❌ Erreur lors de la soumission du pronostic:", error);
      console.error("Détails de l'erreur:", error.response?.data);
      throw error;
    }
  }

  async getPredictionWinners(): Promise<PredictionWinner[]> {
    try {
      const response = await this.apiClient.get("/items/gagnants_pronostics", {
        params: {
          fields: "*,matchId.*",
          filter: {
            status: {
              _eq: "published",
            },
          },
          sort: "-date_created",
          limit: 50,
        },
      });
      const winners = response.data.data || [];
      return winners.map((winner: any) => ({
        id: winner.id,
        phoneNumber: winner.phoneNumber,
        userName: winner.userName,
        matchId: winner.matchId?.id || winner.matchId,
        score: winner.score,
        match:
          winner.matchId && typeof winner.matchId === "object"
            ? winner.matchId
            : undefined,
        dateCreated: winner.date_created,
        prix: winner.prix,
        status: winner.status,
      }));
    } catch (error: any) {
      if (error?.response?.status !== 403) {
        console.error("Error fetching prediction winners:", error);
      }
      return [];
    }
  }

  async getUserName(userId: string, accountId: string): Promise<string> {
    try {
      const response = await this.apiClient.post("/api/balance", {
        compteId: accountId,
        userId,
      });
      return response.data?.name || "";
    } catch (error) {
      console.error("Error fetching user name:", error);
      return "";
    }
  }

  async createPaymentIntent(data: {
    app_id: string;
    mob_app_id: string;
    amount: number;
    phonenumber: string;
    userID: string;
    commercial_id?: string;
    fcm_token?: string;
  }): Promise<PaymentIntentResponse> {
    try {
      const response = await this.apiClient.post(
        "/pay/easy-service/intent",
        data
      );
      return response.data;
    } catch (error) {
      console.error("Error creating payment intent:", error);
      throw error;
    }
  }

  async checkPaymentStatus(intentId: string): Promise<DirectusSession> {
    try {
      const response = await this.apiClient.get(`/items/session/${intentId}`);
      return response.data;
    } catch (error) {
      console.error("Error checking payment status:", error);
      throw error;
    }
  }

  async processWithdrawal(data: {
    code: string;
    user_id: string;
    phone: string;
    name: string;
    compte_id: string;
  }) {
    try {
      console.debug("🔄 [DirectusService] Processing withdrawal with data:", {
        code: data.code,
        userID: data.user_id,
        userPhone: data.phone,
        username: data.name,
        compte_id: data.compte_id,
      });
      console.debug(
        "🔄 [DirectusService] Authorization header:",
        this.apiClient.defaults.headers.common["Authorization"]
      );

      const response = await this.apiClient.post("/api/cashout", {
        code: data.code,
        userID: data.user_id,
        userPhone: data.phone,
        username: data.name,
        compte_id: data.compte_id,
      });

      console.debug("✅ [DirectusService] Withdrawal response:", response.data);

      // Vérifier le champ success dans la réponse (comme Flutter)
      if (response.data.success === true) {
        console.debug("✅ [DirectusService] Withdrawal successful!");
        return response.data;
      } else {
        // Gérer les messages d'erreur spécifiques (comme Flutter)
        const errorMessage = response.data.message || "Une erreur est survenue";

        // Messages d'erreur traduits (comme Flutter lignes 307-316)
        if (errorMessage === "No payout requests found for this customer.") {
          throw new Error("Code de retrait invalide");
        } else if (errorMessage === "Withdrawal request sent to another POS!") {
          throw new Error(
            "Demande effectuée sur un autre point de vente, veuillez contacter le service client"
          );
        } else {
          throw new Error(errorMessage);
        }
      }
    } catch (error: any) {
      console.error("❌ [DirectusService] Error processing withdrawal:", error);

      // Si c'est une erreur qu'on a throw nous-mêmes, la relancer telle quelle
      if (error.message && !error.response) {
        throw error;
      }

      // Sinon, gérer les erreurs réseau
      console.error(
        "❌ [DirectusService] Error response:",
        error.response?.data
      );
      throw new Error("Une erreur est survenue. Veuillez réessayer");
    }
  }

  createWebSocket(_apiKey: string): WebSocket {
    return new WebSocket(WS_URL);
  }
}

export const directusService = new DirectusService();
